import React, { useEffect, useRef, useState } from "react";
import { Button, Flex, Group, Loader, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import "./chat.css";
import ChatMessageInput from "../components/ChatMessageInput";
import ScrollToBottomButton from "../components/ScrollToBottomButton";
import ChatContainer from "../components/ChatContainer";
import useChatSessions, { ChatSessionValues } from "../hooks/useChatSessions";
import { MessageValues } from "../hooks/useMessages";
import { ChatForm } from "../models/ChatSession";
import SessionTabs from "../components/SessionTabs";
import ChatMessages from "../components/ChatMessages";
import ChatHeader from "../components/ChatHeader";
import { useDisclosure } from "@mantine/hooks";
import ResumeDrawer from "../components/ResumeDrawer";
import JobRecommendationDrawer from "../components/JobRecommendationDrawer";
import {
  AiTaskStatus,
  JobRecommendTaskDTO,
  ResumeSummarizeTaskDTO,
} from "../models/Message";
import useMultiDisclosure from "../hooks/useMultiDisclosure";
import JobRecTaskCompleteDialog from "../components/JobRecTaskCompleteDialog";

const Chat: () => React.JSX.Element = (): React.JSX.Element => {
  const [showScroll, setShowScroll] = useState(false);
  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");

  const [resumeDrawerOpened, resumeDrawerHandlers] = useDisclosure(false);
  const [jobRecommendationDrawerOpened, jobRecommendationDrawerHandlers] =
    useDisclosure(false);

  const [jobRecommendTaskStatusDto, setJobRecommendTaskStatusDTO] =
    useState<JobRecommendTaskDTO | null>(null);
  const [resumeSummarizeTaskStatusDto, setResumeSummarizeTaskStatusDto] =
    useState<ResumeSummarizeTaskDTO | null>(null);

  const scrollToBottom = (auto: boolean = false) =>
    viewport.current!.scrollTo({
      top: viewport.current!.scrollHeight + 500,
      behavior: auto ? "auto" : "smooth",
    });

  const changeConnectionStatus = (status: string) => {
    setConnectionStatus((prev) => status);
  };

  const checkScroll = () => {
    const currentScroll = viewport.current;
    if (!currentScroll) return;
    const atBottom =
      currentScroll.scrollTop + currentScroll.clientHeight >=
      currentScroll.scrollHeight - 100;
    setShowScroll(!atBottom);
  };

  const viewport: React.RefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);
  const chatSessionValues: ChatSessionValues = useChatSessions(
    changeConnectionStatus,
    setJobRecommendTaskStatusDTO,
    setResumeSummarizeTaskStatusDto,
  );
  const messageValues: MessageValues = chatSessionValues.messageValues;

  const {
    disclosures: dialogDisclosures,
    open: dialogOpen,
    close: dialogClose,
  } = useMultiDisclosure(
    chatSessionValues?.chatSessions?.map(
      (chatSession) => chatSession.sessionId,
    ) ?? [],
  );

  const form: ChatForm = useForm({
    mode: "controlled",
    initialValues: {
      message: "",
      file: null as File | null,
    },
    validate: {
      message: (value: string) =>
        value.length > 2000
          ? "Message must be 2,000 characters or fewer."
          : null,
    },
  });

  useEffect(() => {
    if (messageValues.messagesLoading) {
      setPreviousScrollHeight(viewport.current!.scrollHeight);
    } else {
      requestAnimationFrame(() => {
        const newScrollHeight = viewport.current!.scrollHeight;
        const scrollDifference = newScrollHeight - previousScrollHeight;
        viewport.current!.scrollTop += scrollDifference;
      });
    }
    if (messageValues.messages.length <= 50 && !messageValues.messagesLoading) {
      scrollToBottom(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageValues.messagesLoading, messageValues.messages.length]);

  useEffect(() => {
    if (
      jobRecommendTaskStatusDto?.chatSessionId &&
      (jobRecommendTaskStatusDto.status === AiTaskStatus.DONE ||
        jobRecommendTaskStatusDto.status === AiTaskStatus.OOPS)
    ) {
      dialogOpen(jobRecommendTaskStatusDto.chatSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRecommendTaskStatusDto]);

  return (
    <>
      {chatSessionValues.chatSessions?.map((chatSession) => (
        <JobRecTaskCompleteDialog
          key={chatSession.sessionId}
          opened={dialogDisclosures[chatSession.sessionId]}
          onClose={() => dialogClose(chatSession.sessionId)}
          jobRecommendTaskStatus={jobRecommendTaskStatusDto}
          switchActiveSession={chatSessionValues.switchActiveSession}
          chatSession={chatSession}
        />
      ))}
      <ResumeDrawer
        drawerOpened={resumeDrawerOpened}
        drawerHandlers={resumeDrawerHandlers}
        chatSessionId={chatSessionValues.activeSession?.sessionId}
        resumeSummarizeTaskStatusDto={resumeSummarizeTaskStatusDto}
        setResumeSummarizeTaskStatusDto={setResumeSummarizeTaskStatusDto}
      />
      <JobRecommendationDrawer
        drawerOpened={jobRecommendationDrawerOpened}
        drawerHandlers={jobRecommendationDrawerHandlers}
        chatSessionId={chatSessionValues.activeSession?.sessionId}
        jobRecommendTaskStatusDto={jobRecommendTaskStatusDto}
        setJobRecommendTaskStatusDTO={setJobRecommendTaskStatusDTO}
        chatForm={form}
        updateHasJobAlert={chatSessionValues.updateHasJobAlert}
      />
      <Group justify="space-evenly" className="chat-page-container">
        <Stack h="100%" w="30%">
          <SessionTabs
            chatSessionValues={chatSessionValues}
            connectionStatus={connectionStatus}
            sessionUserStatuses={chatSessionValues.sessionUserStatuses}
            unreadCounts={chatSessionValues.unreadCounts}
            lastMessages={chatSessionValues.lastMessages}
          />
          <Group grow>
            <Button
              disabled={!chatSessionValues.activeSession}
              onClick={resumeDrawerHandlers.open}
            >
              Resume
            </Button>
            <Button
              disabled={!chatSessionValues.activeSession}
              onClick={jobRecommendationDrawerHandlers.open}
              loading={
                jobRecommendTaskStatusDto?.status === AiTaskStatus.RUNNING &&
                jobRecommendTaskStatusDto.chatSessionId ===
                  chatSessionValues.activeSession?.sessionId
              }
            >
              Job recommendation
            </Button>
          </Group>
        </Stack>
        <Stack h="100%" w="50%" align="center">
          <ChatContainer viewport={viewport} checkScroll={checkScroll}>
            {messageValues.messagesLoading &&
            messageValues.messages.length === 0 ? (
              <Flex h="100%" w="100%" align="center" justify="center">
                <Loader />
              </Flex>
            ) : (
              <Stack w="100%">
                <ChatHeader
                  messageValues={messageValues}
                  chatSessionValues={chatSessionValues}
                />
                <ChatMessages messages={messageValues.messages} />
                {showScroll && (
                  <ScrollToBottomButton scrollToBottom={scrollToBottom} />
                )}
              </Stack>
            )}
          </ChatContainer>
          {!!chatSessionValues.activeSession &&
            chatSessionValues.activeSession.adminUserId && (
              // Show chat message input if there is an assigned active session
              <ChatMessageInput
                form={form}
                sendMessage={(): void => {
                  messageValues.sendMessage(
                    form.getValues().message,
                    form.getValues().file,
                  );
                  chatSessionValues.lastMessages.set(
                    chatSessionValues.activeSession!.sessionId,
                    form.getValues().message,
                  );
                }}
              />
            )}
        </Stack>
      </Group>
    </>
  );
};

export default Chat;
